import { CalendarUnit, IAccountSummaryFormatting } from '@opengov/reporting-types';

/** Id type for table rows and columns (API can send number or string e.g. "categories header") */
export type TableId = number | string;

/** Left "name" column id for roving tabindex */
export const NAME_COL_ID = '__name__';

/** Active cell identified by row/col ids (for keyboard nav) */
export interface ActiveCell {
  rowId: TableId;
  colId: TableId;
}

/** Flattened row in render order (preorder over tree) */
export interface RowMeta {
  row: ISummaryTableRow | null;
  rowId: TableId;
  level?: number;
  parentId?: TableId | null;
}

export type RegisterCellRefCallback = (
  rowId: TableId,
  colId: TableId,
  el: HTMLTableCellElement | null
) => void;

export type OnCellFocusCallback = (rowId: TableId, colId: TableId) => void;

export type UnderlineRole = 'subtotal' | 'sectionTotal' | 'statementTotal' | null;

export interface ITableRows {
  data: ISummaryTableRow[];
  style: ISummaryTableStyle;
  maxColumn: number;
  printCellStyle?: string;
  isDetailsTable: boolean;
  stickyOffset?: number;
  parentBorders?: Record<string, boolean>;
  formatter?: CellFormatter;
  printMode?: boolean;
  showBorders?: ShowBorders;
  /** Keyboard nav: which cell has tabIndex 0 */
  active?: ActiveCell | null;
  /** Keyboard nav: [NAME_COL_ID, ...value column ids] */
  colIds?: TableId[];
  /** Keyboard nav: register cell element for programmatic focus */
  registerCellRef?: RegisterCellRefCallback;
  /** Keyboard nav: sync active when a cell receives focus */
  onCellFocus?: OnCellFocusCallback;
  /** PDF print mode: maps rowGroupTotal row ids to their underline role */
  underlineRoleMap?: Map<TableId, UnderlineRole>;
  /** PDF print mode: underline span min-width in `ch` units, derived from the statementTotal row */
  underlineWidth?: number;
}
export interface ISummaryTableRowStyles {
  rowGroupHeader?: 'rowGroupHeader';
  rowSubHeader?: 'rowSubHeader';
  rowGroupTotal?: 'rowGroupTotal';
  rowHeader?: 'rowHeader';
  row?: 'row';
  rowGroupTotalHeader?: 'rowGroupTotalHeader';
}

export enum ETableContentType {
  RowHeader = 'rowHeader',
  RowSubHeader = 'rowSubHeader',
  RowGroupHeader = 'rowGroupHeader',
  RowGroupTotal = 'rowGroupTotal',
  Row = 'row',
  Col = 'col'
}

export interface ISummaryTableCellStyle {
  name?: string;
  value?: string;
  colGroup?: string;
}

export interface ISummaryTableStyle {
  tableContainer?: string;
  table?: string;
  row?: ISummaryTableRowStyles;
  cell?: ISummaryTableCellStyle;
  header?: ISummaryTableCellStyle;
}

export interface ISummaryTableColumn {
  id: TableId;
  colSpan?: number;
  value?: string;
  format?: EColumnType;
  calendarUnit?: CalendarUnit;
}

export interface ISummaryTableRow {
  id: TableId;
  name?: string;
  type: ETableContentType;
  level?: number;
  col?: ISummaryTableColumn[];
  rows?: ISummaryTableRow[];
  format?: EColumnType;
}

export interface IPrintStyle {
  nameWidth: number;
  fontSize: number;
  showGridlines?: boolean;
  showUnderlines?: boolean;
  keepRowGroupsTogetherOnPageBreaks?: boolean;
  /**
   * When true, the in-browser puppeteer fallback (splitMDSSummaryTable) will choose split
   * boundaries that respect column-group (top-level <th colspan>) boundaries instead of
   * slicing every `maxColumns` leaf cells. The primary split happens earlier in the data
   * pipeline (divideTablePerColumns), but we emit this flag so puppeteer can stay in sync
   * for any oversized table that slipped through.
   */
  keepColumnGroupsTogetherOnPageBreaks?: boolean;
  underlineWidth?: number;
}
export interface ISummaryTable {
  columns: ISummaryTableColumn[];
  data?: ISummaryTableRow[];
  style?: ISummaryTableStyle;
  embedId: string;
  isDetailsTable?: boolean;
  accountSummaryTableFormatting?: IAccountSummaryFormatting;
  printStyle?: IPrintStyle;
}

export interface ShowBorders {
  rows: boolean;
  cols: boolean;
}

/* Here we are using 2 types for percentage format based on what we are getting in API response.
    1.percentage -> for nextGen APIs.
    2.Percent -> for classic APIs. */
export enum EColumnType {
  Currency = 'currency',
  ClassicPercent = 'percent',
  Percentage = 'percentage',
  Numeric = 'numeric',
  Text = 'text',
  Integer = 'integer',
  Date = 'date'
}

export type CellFormatter = (
  value: string | number | null | undefined,
  format?: EColumnType,
  calendarUnit?: CalendarUnit
) => string;
